import "twin.macro";

import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { Checkbox } from "@mui/material";

import _ from "lodash";

import Filters from "@features/filters";
import { usePaginatedProgramsQuery } from "@features/programs";
import ProgramStatusChip from "@features/programs/ProgramStatusChip";
import IndexTable from "@features/ui/indexTable";
import { Program } from "@models/Program";
import { CldImage } from "@services/cloudinary";

const OrderWindowProgramTable = ({
  orderWindowId,
}: {
  orderWindowId: string | null;
}) => {
  const { setValue } = useFormContext();
  const setProgramIds = (ids: string[]) =>
    setValue("programIds", ids, { shouldDirty: true });
  const programIds = useWatch({ name: "programIds" });

  const channelId = useWatch({ name: "channelId" });

  const defaultFilters = {
    orderWindowId,
    channelId,
    sort: "-id",
  };

  const [filter, setFilter] = useState<Record<string, any>>({});

  const params = {
    filter: {
      name: filter.q,
      territoryIds: filter.territories,
      orderWindowIds: filter.orderWindows,
      groupIds: filter.groups && Object.values(filter.groups).flat(),
      isActive: filter.activeStatus && filter.activeStatus !== "inactive",
      isDraft: !filter.activeStatus ? null : filter.activeStatus === "draft",
      channelIds: channelId && [channelId],
    },
    sort: filter.sort,
  };

  const { data: programs = [], ...tableProps } =
    usePaginatedProgramsQuery(params);

  const handleToggle = (id: string) => {
    setProgramIds(_.xor(programIds, [id]));
  };

  const handleSelectAll = () => {
    if (programIds.length === programs.length) {
      setProgramIds([]);
    } else {
      setProgramIds(programs.map((p) => p.id));
    }
  };

  return (
    <div>
      <Filters
        searchTitle="Search Programs"
        slots={["activeStatus", "orderWindows", "groups", "territories"]}
        filterState={filter}
        setFilterState={setFilter}
        defaultValues={defaultFilters}
      />
      <IndexTable
        maxHeight={600}
        rows={programs}
        filterState={filter}
        setFilterState={setFilter}
        columns={[
          {
            id: "select",
            label: (
              <Checkbox
                indeterminate={
                  programIds.length > 0 && programIds.length < programs.length
                }
                checked={
                  programs.length > 0 && programIds.length === programs.length
                }
                onChange={handleSelectAll}
                disabled={programs.length === 0}
              />
            ),
            render: (i, row) => (
              <div tw="align-middle">
                <Checkbox
                  checked={programIds.includes(row.id)}
                  inputProps={{ "aria-labelledby": `item-checkbox-${i}` }}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    handleToggle(row.id);
                    event.stopPropagation();
                  }}
                />
              </div>
            ),
          },
          {
            id: "preview",
            label: "",
            sort: "id",
            reverseSort: true,
            render: (_, row) => (
              <div tw="w-14">
                <CldImage
                  tw="aspect-square w-full object-contain 
        border-2 border-neutral-200
      bg-white rounded-md"
                  publicId={row.cloudinaryId}
                  size="thumbnail"
                />
              </div>
            ),
          },
          {
            id: "name",
            label: "Name",
            sort: "name",
            render: (name) => <h4 tw="text-base font-medium">{name}</h4>,
          },
          {
            id: "_status",
            label: "Status",
            render: (_, row) => <ProgramStatusChip program={row} />,
          },
          {
            id: "territories",
            label: "Territories",
            render: (_, row) =>
              row.isTerritoryExclusive
                ? row.territories
                    .map((t) => t.name)
                    .sort()
                    .join(", ")
                : "Available Nationally",
          },
        ]}
        onRowClick={(prog: Program) => handleToggle(prog.id)}
        {...tableProps}
      />
    </div>
  );
};

export default OrderWindowProgramTable;
